import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import API_CONFIG from '../../config/apiConfig';

// Get the dynamic API base URL
const getApiBaseUrl = (): string => {
  // Use the dynamic configuration
  return API_CONFIG.BASE_URL || API_CONFIG.getApiUrl();
};

// Create axios instance with dynamic base URL
export const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Update the base URL dynamically when it changes (for runtime updates)
if (typeof window !== 'undefined') {
  // Update base URL on page load and navigation
  const updateBaseUrl = () => {
    const newBaseUrl = getApiBaseUrl();
    if (newBaseUrl && apiClient.defaults.baseURL !== newBaseUrl) {
      apiClient.defaults.baseURL = newBaseUrl;
      console.log('[API Client] Updated base URL to:', newBaseUrl);
    }
  };
  
  // Update on load
  updateBaseUrl();
  
  // Update on navigation (for SPAs)
  window.addEventListener('popstate', updateBaseUrl);
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      console.error('[API Client] Got 401 error:', {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        response: error.response?.data,
        currentPath: window.location.pathname
      });
      
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      
      const authStore = (window as any).__authStore?.getState();
      if (authStore?.logout) {
        authStore.logout();
      }
      
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    // Extract error message
    const errorData = error.response?.data as any;
    const message = errorData?.message || error.message || 'An error occurred';
    
    return Promise.reject({
      ...error,
      message,
    });
  }
);

// Helper function for typed API calls
export async function apiRequest<T>(
  config: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.request<T>(config);
  return response.data;
}

// Export common HTTP methods with typing
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => 
    apiRequest<T>({ ...config, method: 'GET', url }),
  
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) => 
    apiRequest<T>({ ...config, method: 'POST', url, data }),
  
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) => 
    apiRequest<T>({ ...config, method: 'PUT', url, data }),
  
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) => 
    apiRequest<T>({ ...config, method: 'PATCH', url, data }),
  
  delete: <T>(url: string, config?: AxiosRequestConfig) => 
    apiRequest<T>({ ...config, method: 'DELETE', url }),
};